// The authenticated shell (SM-722). beforeLoad runs on EVERY navigation:
//
//   1. /api/auth/me          no session → /login (with a redirect back)
//   2. /api/broker/me        memberships + capabilities; re-read when stale
//                            (15 s), so a revoked membership ends the
//                            session on the next click, not the next login
//   3. ?node=                the selected node (default: the highest ACTIVE
//                            membership); its /path breadcrumb decides which
//                            membership covers it and so what the caller may
//                            do there. A node outside the caller's scope
//                            answers the server's 404, which the shell shows.
//   4. ?currency=            GOLD / RED / GREEN; money pages show one. Only
//                            a currency the node trades (/path currencies,
//                            SM-851): any other falls back to its first.
//
// node and currency always end up IN the URL, so every screen is a
// shareable deep link. Child routes' own beforeLoad re-checks the
// capability their screen needs (lib/capabilities.ts guardRoute).
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { CurrencyPresentationProvider, NodeCurrencyImageProvider } from "@/components/currency/CurrencyPresentationProvider";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { ScopeProvider } from "@/components/layout/ScopeContext";
import type { Shell, ShellSearch } from "@/components/layout/guard";
import { NotStaffScreen, NoActiveMembershipScreen, NodeErrorPanel, ShellErrorScreen } from "@/components/layout/ShellScreens";
import { InvitationBanner } from "@/components/layout/InvitationBanner";
import { APIError, setCSRF } from "@/lib/api";
import { fetchMeOrNull, fetchStaffMe, logout } from "@/lib/auth";
import { keys, fetchPath } from "@/lib/queries";
import { membershipOn, resolveScope } from "@/lib/capabilities";
import { classifyStaffMeFailure } from "@/lib/session";
import { DEFAULT_CURRENCY, currencyOnNode, isCurrencyCode, nodeCurrencies } from "@/lib/currency";
import type { StaffMeResponse, StaffPathResponse } from "@/lib/types";

export const Route = createFileRoute("/_app")({
  validateSearch: (search: Record<string, unknown>): ShellSearch => ({
    node: typeof search.node === "string" && search.node.trim() !== "" ? search.node.trim() : undefined,
    currency: isCurrencyCode(search.currency) ? search.currency : undefined,
  }),
  beforeLoad: async ({ context, location, search }): Promise<{ shell: Shell }> => {
    const qc = context.queryClient;
    const me = await qc.ensureQueryData({ queryKey: keys.me(), queryFn: fetchMeOrNull, staleTime: 60_000 });
    if (!me) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }

    const hadStaff = (qc.getQueryData<StaffMeResponse>(keys.staffMe())?.memberships.length ?? 0) > 0;
    let staff: StaffMeResponse;
    try {
      staff = await qc.fetchQuery({ queryKey: keys.staffMe(), queryFn: fetchStaffMe, staleTime: 15_000 });
    } catch (err) {
      const failure = classifyStaffMeFailure(err, hadStaff);
      if (failure === "expired" || failure === "revoked") {
        // Nothing protected may outlive the membership: clear, sign out
        // server-side (revocation), then /login with the reason.
        qc.clear();
        if (failure === "revoked") await logout().catch(() => undefined);
        setCSRF(null);
        throw redirect({ to: "/login", search: { reason: failure } });
      }
      if (failure === "not-staff") {
        // Signed in, but not broker staff: drop anything cached and say so.
        qc.removeQueries({ predicate: (q) => q.queryKey[0] === "broker" });
        return { shell: { kind: "not-staff", me, error: err } };
      }
      return { shell: { kind: "error", me, error: err } };
    }

    const first = resolveScope(staff, search.node);
    if (first.kind === "no-active-membership") {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] === "broker" });
      return { shell: { kind: "no-active", me, staff } };
    }
    const nodeId = first.nodeId;
    const currency = search.currency ?? DEFAULT_CURRENCY;

    // Put the resolved node and currency in the URL (replace), so what the
    // user sees is always a shareable link.
    if (!search.node || !search.currency) {
      const params = new URLSearchParams(location.searchStr);
      params.set("node", nodeId);
      params.set("currency", currency);
      throw redirect({ href: `${location.pathname}?${params.toString()}`, replace: true });
    }

    let path: StaffPathResponse;
    try {
      path = await qc.fetchQuery({ queryKey: keys.path(nodeId), queryFn: () => fetchPath(nodeId), staleTime: 30_000 });
    } catch (err) {
      if (err instanceof APIError && err.status === 401) {
        qc.clear();
        setCSRF(null);
        throw redirect({ to: "/login", search: { reason: "expired" } });
      }
      return { shell: { kind: "node-error", me, staff, nodeId, currency, error: err } };
    }
    // A currency the node does not trade (an old ?currency=RED link on a
    // broker node, which trades GOLD only) lands on the node's first (replace).
    const currencies = nodeCurrencies(path.currencies, nodeId);
    const onNode = currencyOnNode(currency, currencies);
    if (onNode !== currency) {
      const params = new URLSearchParams(location.searchStr);
      params.set("currency", onNode);
      throw redirect({ href: `${location.pathname}?${params.toString()}`, replace: true });
    }
    const res = resolveScope(staff, nodeId, path);
    if (res.kind !== "ok") {
      return { shell: { kind: "node-error", me, staff, nodeId, currency, error: null } };
    }
    const node = path.path.find((p) => p.broker_id === nodeId) ?? path.path[path.path.length - 1];
    return {
      shell: {
        kind: "ready",
        scope: {
          me,
          staff,
          nodeId,
          path,
          node,
          capabilities: res.capabilities,
          onMembershipNode: res.onMembershipNode,
          membership: membershipOn(staff, nodeId),
          currency,
          currencies,
        },
      },
    };
  },
  component: AppLayout,
});

function AppLayout() {
  const { shell } = Route.useRouteContext();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (shell.kind === "not-staff") return <NotStaffScreen me={shell.me} error={shell.error} />;
  if (shell.kind === "no-active") return <NoActiveMembershipScreen me={shell.me} staff={shell.staff} />;
  if (shell.kind === "error") return <ShellErrorScreen me={shell.me} error={shell.error} />;

  return (
    <CurrencyPresentationProvider>
      {shell.kind === "ready" ? (
        <ScopeProvider value={shell.scope}>
          {/* SM-978: every GOLD figure draws the selected node's image */}
          <NodeCurrencyImageProvider nodeId={shell.scope.nodeId}>
            <div className="admin-shell">
              <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
              <div className="admin-main">
                <TopBar onMenuClick={() => setMobileNavOpen(true)} />
                <main id="main" className="admin-main-content" tabIndex={-1}>
                  <InvitationBanner />
                  <Outlet />
                </main>
              </div>
            </div>
          </NodeCurrencyImageProvider>
        </ScopeProvider>
      ) : (
        <NodeErrorPanel me={shell.me} staff={shell.staff} nodeId={shell.nodeId} error={shell.error} />
      )}
    </CurrencyPresentationProvider>
  );
}
